import {
    useCallback,
    useMemo,
    useState,
    type Dispatch,
    type SetStateAction,
} from "react";
import { uuidv7 } from "uuidv7";
import { isAxiosError } from "axios";
import type { WorkspaceSchema } from "@common/schemas/workspace.schema";
import { VIEW_ONLY_WRITE_REFUSAL_MESSAGE } from "@common/userWorkspaceRoles";
import {
    isViewOnlyRefusalError,
    VIEW_ONLY_TOAST_KEY,
} from "@lib/viewOnlyRefusal";
import type { ChartOfAccountsTemplate } from "@common/types/chartOfAccountsTemplate";
import { pluralizeWithCount } from "@common/helpers/string";
import {
    useApplyCoaTemplateMutation,
    usePreviewApplyCoaTemplateQuery,
} from "@api/coaTemplate.api";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useApplyCoaTemplateEntityTree } from "./useApplyCoaTemplateEntityTree";
import { ApplyCoaTemplateSelectEntitiesStep } from "./ApplyCoaTemplateSelectEntitiesStep";
import { ApplyCoaTemplatePreviewStep } from "./ApplyCoaTemplatePreviewStep";
import { buildSelectedPreviewEntries } from "./applyCoaTemplatePreviewEntries";
import styles from "./ApplyCoaTemplateDrawer.module.scss";

interface Props {
    template: ChartOfAccountsTemplate | undefined;
    workspaces: WorkspaceSchema[] | undefined;
    step: 1 | 2;
    setStep: Dispatch<SetStateAction<1 | 2>>;
    onClose: () => void;
    onAddEntity: () => void;
    onEditTemplate: () => void;
}

export const ApplyCoaTemplateWizardBody = ({
    template,
    workspaces,
    step,
    setStep,
    onClose,
    onAddEntity,
    onEditTemplate,
}: Props) => {
    const { toast: successToast, error: errorToast } = useToaster();
    const applyMutation = useApplyCoaTemplateMutation(template?.id);

    const [selectedEntityIds, setSelectedEntityIds] = useState(
        () => new Set<string>(),
    );
    const [appliedEntityIds, setAppliedEntityIds] = useState(
        () => new Set<number>(),
    );
    const [applyingEntityId, setApplyingEntityId] = useState<number | null>(
        null,
    );
    const [isApplyingBulk, setIsApplyingBulk] = useState(false);

    const { tableData, applicableEntityIds, isLoading, isError } =
        useApplyCoaTemplateEntityTree({ workspaces, template });

    // Single source of truth for the selected ids that are still applicable.
    // Both the step footer count and the step-2 stub use this, and FE-2 will
    // read it to build the preview/apply payload — so a stale or
    // no-longer-applicable id can never leak downstream.
    const selectedApplicableEntityIds = useMemo(
        () =>
            [...selectedEntityIds].filter((id) => applicableEntityIds.has(id)),
        [selectedEntityIds, applicableEntityIds],
    );

    const handleContinue = useCallback(() => {
        setStep(2);
    }, [setStep]);

    const handleBack = useCallback(() => {
        setStep(1);
    }, [setStep]);

    const entityIds = useMemo(
        () => selectedApplicableEntityIds.map(Number),
        [selectedApplicableEntityIds],
    );

    const previewEntries = useMemo(
        () =>
            buildSelectedPreviewEntries({
                tableData,
                selectedApplicableEntityIds,
            }),
        [tableData, selectedApplicableEntityIds],
    );

    const handleApply = useCallback(
        async ({ entityIds: targetIds }: { entityIds: number[] }) => {
            if (!template || isApplyingBulk) {
                return;
            }
            const pendingEntityIds = [...new Set(targetIds)].filter(
                (id) => !appliedEntityIds.has(id),
            );
            if (pendingEntityIds.length === 0) {
                return;
            }

            setIsApplyingBulk(true);
            const applyToEntities = async () => {
                const applyInvocationId = uuidv7();
                let accountsCreated = 0;
                let accountsUpdated = 0;
                let succeededEntities = 0;
                let failedEntities = 0;
                let hasViewOnlyFailure = false;
                let firstFailureMessage: string | null = null;
                for (const entityId of pendingEntityIds) {
                    try {
                        setApplyingEntityId(entityId);
                        const { results } = await applyMutation.mutateAsync({
                            entityIds: [entityId],
                            applyInvocationId,
                        });
                        for (const result of results) {
                            accountsCreated += result.accountsCreated;
                            accountsUpdated += result.accountsUpdated;
                        }
                        succeededEntities += 1;
                        setAppliedEntityIds((prev) =>
                            new Set(prev).add(entityId),
                        );
                    } catch (err) {
                        // A view-only refusal is the same for every entity, so
                        // it is surfaced once via the shared toast slot instead
                        // of being counted into the "N failed" aggregate.
                        if (isViewOnlyRefusalError(err)) {
                            hasViewOnlyFailure = true;
                            continue;
                        }
                        failedEntities += 1;
                        const responseMessage = isAxiosError(err)
                            ? err.response?.data?.message
                            : undefined;
                        const message =
                            typeof responseMessage === "string"
                                ? responseMessage
                                : "Failed to apply template";
                        firstFailureMessage = firstFailureMessage ?? message;
                    }
                }
                if (succeededEntities > 0) {
                    successToast(
                        `Applied "${template.name}": ${pluralizeWithCount("account", accountsCreated)} created and ${pluralizeWithCount("account", accountsUpdated)} updated`,
                    );
                }
                if (hasViewOnlyFailure) {
                    errorToast(
                        VIEW_ONLY_WRITE_REFUSAL_MESSAGE,
                        VIEW_ONLY_TOAST_KEY,
                    );
                }
                if (failedEntities > 0) {
                    const failedBaseMessage = `${pluralizeWithCount("entity", failedEntities)} failed to apply`;
                    errorToast(
                        firstFailureMessage
                            ? `${failedBaseMessage}: ${firstFailureMessage}`
                            : failedBaseMessage,
                    );
                }
            };
            await applyToEntities().finally(() => {
                setIsApplyingBulk(false);
                setApplyingEntityId(null);
            });
        },
        [
            appliedEntityIds,
            applyMutation,
            errorToast,
            isApplyingBulk,
            successToast,
            template,
        ],
    );

    // Only fetch the preview while on step 2; passing a null templateId keeps the
    // query disabled on the select step.
    const { data: preview, isError: isPreviewError } =
        usePreviewApplyCoaTemplateQuery(
            step === 2 ? (template?.id ?? null) : null,
            { entityIds },
        );

    if (step === 2) {
        // Only a real query error shows the error copy. A missing `preview`
        // (still loading, or the query disabled — e.g. a future zero-selection
        // path) falls through to the loader so it can't surface a false error.
        if (isPreviewError) {
            return (
                <div className={styles.stepRoot}>
                    <div className={styles.subtitleContainer}>
                        Step 2 of 2 · Preview selection
                    </div>
                    <EmptyState
                        header="Couldn't load the template preview"
                        ctaText="Back"
                        action={handleBack}
                    />
                </div>
            );
        }

        if (!preview) {
            return (
                <div className={styles.stepRoot}>
                    <div className={styles.subtitleContainer}>
                        Step 2 of 2 · Preview selection
                    </div>
                    <Loader />
                </div>
            );
        }

        return (
            <ApplyCoaTemplatePreviewStep
                preview={preview}
                entries={previewEntries}
                appliedEntityIds={appliedEntityIds}
                applyingEntityId={applyingEntityId}
                isApplyingBulk={isApplyingBulk}
                onBack={handleBack}
                onApply={handleApply}
                onClose={onClose}
            />
        );
    }

    return (
        <ApplyCoaTemplateSelectEntitiesStep
            tableData={tableData}
            applicableEntityIds={applicableEntityIds}
            entityLegalType={template?.entityLegalType}
            isLoading={isLoading}
            isError={isError}
            selectedEntityIds={selectedEntityIds}
            setSelectedEntityIds={setSelectedEntityIds}
            selectedApplicableEntityIds={selectedApplicableEntityIds}
            onContinue={handleContinue}
            onAddEntity={onAddEntity}
            onEditTemplate={onEditTemplate}
        />
    );
};
